import { AspectRatio, Flex, Image, Separator, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useI18nLn } from '~/hooks/store';
import type { PlayTicket, ShippingSpec } from '~/types/resources';
import { DeliveryBundle } from '~/types/resources/deliveryBundle';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';
import { TotalAmountContainer } from '../common/TotalAmountContainer';
import { BillingDetail } from './BillingDetail';

type Props = {
  gachaDetail: GachaDetail;
  playAmount: number;
  productTotalPrice: number;
  bundleAmount: number;
  discountedPrice: number;
  shippingSpec: ShippingSpec | undefined;
  totalPrice: number;
  deliveryBundles: DeliveryBundle[];
  deliveryInfo: DeliveryInfo | null;
  deliveryInfoOnOpen: () => void;
  skipDelivery: boolean;
  isPlayTicketPayment: boolean;
  isLocalPickupOnly?: boolean;
  selectedPlayTicket: PlayTicket | null;
  deliveryCharge: number;
};

export const SelectProductInfo: React.FC<Props> = (props: Props) => {
  const { t, i18nLn } = useI18nLn();
  const {
    gachaDetail,
    playAmount,
    productTotalPrice,
    bundleAmount,
    discountedPrice,
    shippingSpec,
    totalPrice,
    deliveryBundles,
    deliveryInfo,
    deliveryInfoOnOpen,
    skipDelivery,
    isPlayTicketPayment,
    isLocalPickupOnly,
    selectedPlayTicket,
    deliveryCharge,
  } = props;

  const playAmountText: string = useMemo(() => {
    if (playAmount === 1) {
      return t('pages.gacha.play.single', { amount: playAmount });
    }
    return t('pages.gacha.play.multi', { amount: playAmount });
  }, [playAmount, i18nLn]);

  return (
    <Flex
      flexDir={{ base: 'column', md: 'row' }}
      border="1px"
      borderColor="border"
      p="16px"
      rounded="10px"
      gap={3}
    >
      <Flex flex={1} flexDir="column">
        <AspectRatio ratio={16 / 9}>
          <Image
            src={gachaDetail.thumbnail_image_url}
            alt={`${gachaDetail.name}のサムネイル画像`}
          />
        </AspectRatio>
      </Flex>
      <Flex flex={2} flexDir="column" align="flex-end" gap={2}>
        <Text
          fontSize="18px"
          fontWeight="bold"
          w="full"
          textTransform="capitalize"
        >
          {gachaDetail.name}
        </Text>
        <TotalAmountContainer
          totalAmount={productTotalPrice}
          visual="subtotal"
          title={playAmountText}
        />
        <Flex
          w="full"
          flexDir="column"
          align="flex-end"
          gap={2}
          display={isLocalPickupOnly ? 'none' : 'flex'}
        >
          {gachaDetail.gacha_delivery_info && (
            <BillingDetail
              deliveryInfoOnOpen={deliveryInfoOnOpen}
              isPlayTicketPayment={isPlayTicketPayment}
              skipDelivery={skipDelivery}
              bundleAmount={bundleAmount}
              discountedPrice={discountedPrice}
              playAmount={playAmount}
              deliveryBundles={deliveryBundles}
              gachaDetail={gachaDetail}
              deliveryInfo={deliveryInfo}
              selectedPlayTicket={selectedPlayTicket}
              shippingSpec={shippingSpec}
              deliveryCharge={deliveryCharge}
            />
          )}
          <Separator />
          <TotalAmountContainer totalAmount={totalPrice} visual="total" />
        </Flex>
      </Flex>
    </Flex>
  );
};
